import { useMemo } from 'react';

import { aiMimeTypes, PlanStateSchema, PlaybookAiRunResultSchema } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { useDeepCompareEffect } from 'react-use';
import { Apollo } from 'src/gql/generated';

const PLAYBOOK_RUN_FENCE_REGEX = new RegExp(
  `\`\`\`${aiMimeTypes.jsonPlaybookRun}\\s*\\n([\\s\\S]*?)\\n\`\`\``,
  'g'
);

/**
 * Registers one DocPlaybooksUpdated subscription per document that has a
 * playbook run card in the thread, so run cards receive live rule-job updates
 * through the normalized cache without per-card subscriptions or polling.
 */
export function useDocPlaybookRunSubscriptions({
  messages,
}: {
  messages: Pick<Apollo.AgentMessage, 'planState' | 'text'>[];
}) {
  const client = useApolloClient();
  const documentIds = useMemo(
    () => extractPlaybookRunDocumentIdsFromMessages(messages),
    [messages]
  );

  /**
   * Deep-compared so the effect re-runs only when the set of subscribed
   * documents actually changes, not on every messages identity change while
   * a response streams in.
   */
  useDeepCompareEffect(() => {
    if (!documentIds.length) return;

    const subscriptions = documentIds.map(documentId =>
      client
        .subscribe<Apollo.OnDocPlaybooksUpdatedSubscription>({
          query: Apollo.OnDocPlaybooksUpdatedDocument,
          variables: { documentId },
        })
        // Results are written to the normalized cache; observers only need to keep the subscription open.
        .subscribe({ error: () => undefined })
    );

    return () => subscriptions.forEach(subscription => subscription.unsubscribe());
  }, [documentIds]);
}

/**
 * Extracts the unique document IDs of every playbook run card in a thread's
 * messages, checking both message text and harness node output display text.
 */
export function extractPlaybookRunDocumentIdsFromMessages(
  messages: Pick<Apollo.AgentMessage, 'planState' | 'text'>[]
): string[] {
  const documentIds = new Set<string>();

  for (const message of messages) {
    addPlaybookRunDocumentIds(documentIds, message.text);

    if (!message.planState) continue;
    const parsedPlanState = PlanStateSchema.safeParse(message.planState);
    if (!parsedPlanState.success) continue;

    addPlaybookRunDocumentIds(documentIds, parsedPlanState.data.plannerText);
    for (const nodeOutput of Object.values(parsedPlanState.data.nodeOutputs)) {
      addPlaybookRunDocumentIds(documentIds, nodeOutput.displayText);
    }
  }

  return [...documentIds];
}

function addPlaybookRunDocumentIds(documentIds: Set<string>, markdown: string | null | undefined) {
  if (!markdown) return;

  for (const match of markdown.matchAll(PLAYBOOK_RUN_FENCE_REGEX)) {
    const [, rawJson] = match;
    if (!rawJson) continue;

    const parsed = parsePlaybookRunJson(rawJson);
    if (!parsed.success) continue;
    documentIds.add(parsed.data.documentId);
  }
}

function parsePlaybookRunJson(rawJson: string) {
  try {
    return PlaybookAiRunResultSchema.safeParse(JSON.parse(rawJson));
  } catch {
    return { success: false } as const;
  }
}
